import { useState, useEffect, useCallback, useRef } from 'react';
import { userSettingsService } from '../services/api';
import { useAuth } from './useAuth';

// Default interviewer name
const DEFAULT_INTERVIEWER_NAME = '吉松';

interface UserSettings {
  interviewerName: string;
  displayName: string;
  language: string;
  timezone: string;
}

interface UseUserSettingsReturn {
  interviewerName: string;
  displayName: string;
  loading: boolean;
  error: string | null;
  updateInterviewerName: (name: string) => Promise<void>;
  updateDisplayName: (name: string) => Promise<void>;
  updateSettings: (settings: {
    interviewerName?: string;
    displayName?: string;
    language?: 'ja' | 'en';
    timezone?: string;
  }) => Promise<void>;
  refetch: () => Promise<void>;
}

/**
 * Hook to manage user settings including interviewer name
 * Settings are persisted in Firestore via the user-settings API
 */
export function useUserSettings(): UseUserSettingsReturn {
  const { user } = useAuth();
  const [settings, setSettings] = useState<UserSettings>({
    interviewerName: DEFAULT_INTERVIEWER_NAME,
    displayName: '',
    language: 'ja',
    timezone: 'Asia/Tokyo',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasFetched = useRef(false);

  // Load settings when user is authenticated
  useEffect(() => {
    if (user && !hasFetched.current) {
      loadSettings();
    } else if (!user) {
      // Reset to defaults when logged out
      setSettings({
        interviewerName: DEFAULT_INTERVIEWER_NAME,
        displayName: '',
        language: 'ja',
        timezone: 'Asia/Tokyo',
      });
      setLoading(false);
      hasFetched.current = false;
    }
  }, [user]);

  const loadSettings = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      console.log('[useUserSettings] Loading settings for user:', user.uid);
      const data = await userSettingsService.get();
      console.log('[useUserSettings] Loaded settings:', data);

      setSettings({
        interviewerName: data.interviewerName || DEFAULT_INTERVIEWER_NAME,
        displayName: data.displayName || '',
        language: data.language || 'ja',
        timezone: data.timezone || 'Asia/Tokyo',
      });
      hasFetched.current = true;
    } catch (err) {
      console.error('[useUserSettings] Error loading settings:', err);
      setError(err instanceof Error ? err.message : 'Failed to load settings');
      // Keep default values on error
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = useCallback(async (updates: {
    interviewerName?: string;
    displayName?: string;
    language?: 'ja' | 'en';
    timezone?: string;
  }) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    console.log('[useUserSettings] Updating settings:', updates);

    try {
      await userSettingsService.update(updates);

      // Update local state
      setSettings((prev) => ({
        ...prev,
        ...updates,
      }));

      console.log('[useUserSettings] Settings updated successfully');
    } catch (err) {
      console.error('[useUserSettings] Error updating settings:', err);
      throw err;
    }
  }, [user]);

  const updateInterviewerName = useCallback(async (name: string) => {
    await updateSettings({ interviewerName: name || DEFAULT_INTERVIEWER_NAME });
  }, [updateSettings]);

  const updateDisplayName = useCallback(async (name: string) => {
    await updateSettings({ displayName: name });
  }, [updateSettings]);

  const refetch = useCallback(async () => {
    hasFetched.current = false;
    await loadSettings();
  }, [user]);

  return {
    interviewerName: settings.interviewerName,
    displayName: settings.displayName,
    loading,
    error,
    updateInterviewerName,
    updateDisplayName,
    updateSettings,
    refetch,
  };
}
